<template>
  <span>
    <v-menu
      v-if="!mobileMode"
      v-model="menuOpen"
      :close-on-content-click="false"
      left
      max-width="95%"
    >
      <template v-slot:activator="{ on: menu, attrs }">
        <v-tooltip bottom color="primary" :disabled="!showTooltip">
          <template #activator="{ on: tooltip }">
            <v-icon
              :size="desktopIconSize"
              :color="color"
              :class="containerClass"
              v-bind="attrs"
              v-on="{...tooltip, ...menu}"
              :tabindex="removeFromTabIndex ? '-1' : ''"
              :id="appendElementId ? `column-adjuster-button-${appendElementId}` : undefined">
              mdi-application-edit
            </v-icon>
          </template>
          <span class="subtitle-1 white--text" style="white-space: pre-line">
            {{$t('adjustColumns')}}
          </span>
        </v-tooltip>
      </template>

      <ColumnAdjusterCard
        v-if="menuOpen"
        v-bind="cardProps"
        @apply="apply"
        @reset="reset">
        <template v-if="$scopedSlots.default" #default="slotProps">
          <slot v-bind="slotProps"/>
        </template>
      </ColumnAdjusterCard>
    </v-menu>

    <template v-else>
      <Icon
        :iconColor="color"
        :margin="containerClass"
        icon="mdi-application-edit"
        :tooltipText="$t('adjustColumns')"
        tooltipColor="primary"
        @icon-clicked="openDialog"
        :small="false"
      />

      <Dialog class="d-inline" :stateId="dialogId" @close="$emit('closed')">
        <ColumnAdjusterCard
          v-bind="cardProps"
          mobileMode
          @close="closeDialog"
          @apply="apply"
          @reset="reset">
          <template v-if="$scopedSlots.default" #default="slotProps">
            <slot v-bind="slotProps"/>
          </template>
        </ColumnAdjusterCard>
      </Dialog>
    </template>
  </span>
</template>

<script>
import { ColumnSet } from '@/model/DataTableColumn.js'
import { uniqueDialogId, getMenuContainerStyle } from '@/utils/componentHelpers'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'ColumnAdjuster',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    ColumnAdjusterCard: () => import('@/components/core/ColumnAdjusterCard.vue')
  },

  props: {
    propColumnSet: ColumnSet,
    isOpen: {
      type: Boolean,
      required: false,
      default: false
    },
    showReset: {
      type: Boolean,
      required: false,
      default: false
    },
    removeFromTabIndex: {
      type: Boolean,
      required: false,
      default: false
    },
    appendElementId: {
      type: String,
      required: false,
      default: undefined
    },
    elementId: {
      type: String,
      required: false,
      default: undefined
    },
    containerClass: {
      type: String,
      required: false,
      default: 'mr-1'
    },
    containerStyle: {
      type: Object,
      required: false,
      default: undefined
    },
    color: {
      type: String,
      required: false,
      default: 'primary'
    },
    dialogColor: {
      type: String,
      required: false,
      default: 'primary'
    },
    desktopIconSize: {
      type: String,
      required: false,
      default: undefined
    },
    showTooltip: {
      type: Boolean,
      required: false,
      default: true
    },
    mobileCompatible: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  data: () => ({
    menuOpen: false,
    dialogId: uniqueDialogId('column-adjuster')
  }),

  computed: {
    ...mapGetters('dialog', ['hasOpenDialogById']),

    mobileMode () {
      return this.mobileCompatible || this.$vuetify.breakpoint.smAndDown
    },

    menuContainerStyle () {
      return getMenuContainerStyle(this.$vuetify.breakpoint.width, this.$vuetify.breakpoint.height)
    },

    cardProps () {
      return {
        propColumnSet: this.propColumnSet,
        showReset: this.showReset,
        dialogColor: this.dialogColor,
        containerStyle: this.containerStyle ?? this.menuContainerStyle,
        elementId: this.elementId,
        appendElementId: this.appendElementId
      }
    }
  },

  watch: {
    isOpen (val) {
      if (val) this.openMenu()
    },

    menuOpen (val) {
      if (!val) this.$emit('closed')
    },

    mobileMode (val) {
      if (val) {
        this.menuOpen = false
      } else if (this.hasOpenDialogById(this.dialogId)) {
        this.$emit('closed')
      }
    }
  },

  methods: {
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),

    apply (columnSet) {
      this.menuOpen = false
      this.$emit('new-columns-selected', columnSet.clone())
      if (this.mobileMode) this.closeDialog()
    },

    reset () {
      this.menuOpen = false
      this.$emit('reset')
      if (this.mobileMode) this.closeDialog()
    },

    openDialog () {
      this.openOrUpdateDialog(this.dialogId)
    },

    closeDialog () {
      this.closeDialogsAtOrAbove(this.dialogId)
    },

    openMenu () {
      if (this.mobileMode) {
        this.openDialog()
      } else {
        this.menuOpen = true
      }
    }
  }
}
</script>
